import { useStorage } from '@vueuse/core'
import { v4 as uuidv4 } from 'uuid'
import type { CartItem, CartFormInput } from '@/types/Cart'

import ingredients from './ingredients.json'

export interface CartSuggestion {
  name: string
  cartId?: string
}

const INPUT_COLLAPSED_PLACEHOLDER = ' +   ADD'

export function useCart() {
  const cart = useStorage<CartItem[]>('juss-cart', [])
  const _selectedIngredients = useStorage<Record<string, number>>('juss-selectedIngredients', {})
  const loading = ref(true)

  const cartInput = ref<CartFormInput>({ name: '' })
  const cartInputRef = ref<HTMLInputElement>()
  const cartInputFocussed = ref(false)
  const cartInputExpanded = ref(false)
  const cartInputPlaceholder = ref(INPUT_COLLAPSED_PLACEHOLDER)

  const completeExpanded = ref(false)

  const suggestions = reactive<Record<'From Cart' | 'Suggestions', CartSuggestion[]>>({
    'From Cart': [],
    Suggestions: [],
  })

  const sortedCart = computed({
    get: () => cart.value.sort((a, b) => Number(a.complete) - Number(b.complete)),
    set: (newCart) => (cart.value = newCart),
  })

  const _incompleteCart = computed(() => sortedCart.value.filter(({ complete }) => !complete))
  const _completeCart = computed(() => sortedCart.value.filter(({ complete }) => complete))

  // writable computed refs
  const incompleteCart = computed({
    get: () => _incompleteCart.value,
    set: (newIncomplete) => (cart.value = [...newIncomplete, ..._completeCart.value]),
  })
  const completeCart = computed({
    get: () => _completeCart.value,
    set: (newComplete) => (cart.value = [..._incompleteCart.value, ...newComplete]),
  })

  const sortedSelectedIngredients = computed(() => {
    return Object.entries(_selectedIngredients.value)
      .sort((a, b) => a[1] - b[1])
      .map((x) => x[0])
  })

  onMounted(() => (loading.value = false))

  // backwards compatibility between old storage key
  // TODO: remove after some time
  onMounted(() => {
    const oldCartStorage = window.localStorage.getItem('cart')
    const oldCart = (oldCartStorage ? JSON.parse(oldCartStorage) : []) as CartItem[]
    if (cart.value.length === 0 && oldCart.length > 0) cart.value = oldCart
  })

  watch(
    [cartInput, sortedCart],
    () => {
      const search = cartInput.value.name
      suggestions['From Cart'] = []
      suggestions.Suggestions = []
      if (!search && !cartInputFocussed.value) cartInputExpanded.value = false
      if (search.length >= 2) {
        suggestions['From Cart'] = sortedCart.value
          .filter(({ name, complete }) => complete && name.toLowerCase().includes(search.toLowerCase()))
          .map(({ id, name }) => ({ cartId: id, name }))

        const cartItems = sortedCart.value.map(({ name }) => name.toLowerCase())
        suggestions.Suggestions = filterIngredients(search)
          .filter((name) => !cartItems.includes(name.toLowerCase()))
          .map((name) => ({ name }))
      }
    },
    { deep: true },
  )

  function toggleComplete(item: CartItem) {
    const index = cart.value.findIndex((i) => i.id === item.id)
    if (index === -1) return
    cart.value[index] = { ...item, complete: !item.complete, updated: new Date().toISOString() }
    return cart.value[index]
  }

  function addItem(item: CartFormInput) {
    // add ingredients to selected ingredients list for better sorting
    if (ingredients.includes(item.name)) _selectedIngredients.value[item.name] = Date.now()

    const newItem = {
      id: uuidv4(),
      ...item,
      complete: false,
      created: new Date().toISOString(),
      updated: new Date().toISOString(),
    } as CartItem
    cart.value.push(newItem)
    return newItem
  }

  function removeItem(item: CartItem) {
    const index = cart.value.findIndex((i) => i.id === item.id)
    if (index === -1) return
    cart.value.splice(index, 1)
  }

  function updateItem(item: CartItem) {
    const index = cart.value.findIndex((i) => i.id === item.id)
    if (index === -1) return
    cart.value[index] = { ...cart.value[index], ...item, updated: new Date().toISOString() }
  }

  function filterIngredients(search?: string) {
    const list = ingredients.filter((i) => (search ? i.toLowerCase().includes(search.toLowerCase()) : true))
    const selected = list
      .filter((i) => sortedSelectedIngredients.value.includes(i))
      .sort((a, b) => {
        const aIndex = sortedSelectedIngredients.value.indexOf(a)
        const bIndex = sortedSelectedIngredients.value.indexOf(b)
        return bIndex > aIndex ? 1 : -1
      })
    const rest = list
      .filter((i) => !sortedSelectedIngredients.value.includes(i))
      .sort((a, b) => {
        if (search) {
          const key = search.toLowerCase()
          const isGoodMatchA = a.toLowerCase().startsWith(key)
          const isGoodMatchB = b.toLowerCase().startsWith(key)
          if (isGoodMatchA !== isGoodMatchB) return isGoodMatchA ? -1 : 1 // XOR
        }
        return a.localeCompare(b)
      })
    return [...selected, ...rest]
  }

  function randomIngredient() {
    const list = sortedSelectedIngredients.value.length > 3 ? sortedSelectedIngredients.value : filterIngredients()
    return list[Math.floor(Math.random() * list.length)]
  }

  function cartInputFocusIn() {
    cartInputPlaceholder.value = randomIngredient() + '?'
    cartInputFocussed.value = true
    cartInputExpanded.value = true
  }

  function cartInputFocusOut() {
    cartInputPlaceholder.value = INPUT_COLLAPSED_PLACEHOLDER
    cartInputFocussed.value = false
    if (!cartInput.value.name) cartInputExpanded.value = false
  }

  return {
    cart,
    loading,
    sortedCart,
    incompleteCart,
    completeCart,
    cartInput,
    cartInputRef,
    cartInputExpanded,
    cartInputPlaceholder,
    completeExpanded,
    suggestions,
    toggleComplete,
    addItem,
    removeItem,
    updateItem,
    filterIngredients,
    cartInputFocusIn,
    cartInputFocusOut,
  }
}
